/**
 * TabNav: underline tabs for the Learning Center (plan section 13; design
 * skill "Tabs (underline)" recipe). Reads LMS_TABS so the nav can never list
 * a tab LmsApp does not route. The two adminOnly tabs (Team, Manage) are
 * listed only when GET /api/lms/me says isLmsAdmin; they stay hidden while
 * that answer loads and after it fails (the server re-checks every admin
 * route anyway).
 *
 * Hot file (plan 15.12): WP-03 only.
 */
import { NavLink } from 'react-router-dom';
import { LMS_TABS, lmsTabHref } from '../tabs/config';
import { useLmsMe } from '../hooks/useLmsMe';

const tabCls = (active: boolean) =>
  `inline-flex min-h-[44px] items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:min-h-0 sm:py-2 ${
    active
      ? 'border-primary-600 text-primary-700 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
  }`;

export function TabNav() {
  const { isLmsAdmin } = useLmsMe();
  const tabs = LMS_TABS.filter((tab) => !tab.adminOnly || isLmsAdmin);

  return (
    <nav
      className="flex flex-shrink-0 gap-1 overflow-x-auto border-b border-gray-200 px-4 dark:border-gray-700 sm:px-6 lg:px-8"
      aria-label="Learning Center sections"
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        return (
          <NavLink
            key={tab.key}
            to={lmsTabHref(tab)}
            // Home is the index route: without `end` it would read as active on every /lms page.
            end={tab.path === ''}
            data-testid={`lms-tab-${tab.key}`}
            className={({ isActive }) => tabCls(isActive)}
          >
            <Icon size={16} aria-hidden="true" />
            {tab.navLabel}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default TabNav;
